/**
 * The glyphs the кружочки handoff draws, as inline SVG.
 *
 * `lucide-react` supplies every other icon in the app, and it is the right
 * choice for a toolbar. These are not toolbar icons: the handoff asks for the
 * filled, heavy shapes a recording UI uses — a solid microphone, a solid play
 * triangle, a paper plane — and lucide's are uniformly thin outlines. Drawing
 * these by hand is what keeps the composer looking like the design instead
 * of like a settings page.
 *
 * All of them inherit `currentColor` and take a size, so they behave like the
 * lucide icons beside them.
 */

interface IconProps {
  size?: number;
}

export function MicIcon({ size = 18 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <rect x="9" y="2" width="6" height="12" rx="3" />
      <path d="M6 11a6 6 0 0012 0" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" />
      <line x1="12" y1="19" x2="12" y2="22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="8" y1="22" x2="16" y2="22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

export function VideoIcon({ size = 18 }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="6" width="13" height="12" rx="3" />
      <path d="M16 10l5-3v10l-5-3" />
    </svg>
  );
}

export function PlayIcon({ size = 16 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M6 4l14 8-14 8z" />
    </svg>
  );
}

export function PauseIcon({ size = 15 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <rect x="5" y="4" width="5" height="16" rx="1" />
      <rect x="14" y="4" width="5" height="16" rx="1" />
    </svg>
  );
}

export function SendIcon({ size = 20 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M3.2 20.5l18-8.5-18-8.5 4 8.5-4 8.5z" />
    </svg>
  );
}

export function TrashIcon({ size = 16 }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 7h16" />
      <path d="M9 7V4h6v3" />
      <path d="M6 7l1 13h10l1-13" />
    </svg>
  );
}

/** Two arrows around a lens: front camera ⇄ back camera. */
export function FlipCameraIcon({ size = 15 }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M17 3l3 3-3 3" />
      <path d="M20 6H8a4 4 0 00-4 4v1" />
      <path d="M7 21l-3-3 3-3" />
      <path d="M4 18h12a4 4 0 004-4v-1" />
    </svg>
  );
}

/** One glyph with two states, so the muted variant is never a different shape. */
export function SpeakerIcon({ size = 12, muted = false }: IconProps & { muted?: boolean }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 9v6h4l5 5V4L8 9H4z" fill="currentColor" stroke="none" />
      {muted ? <line x1="15" y1="8" x2="21" y2="16" /> : <path d="M16.5 8.5a4 4 0 010 7" />}
    </svg>
  );
}

/**
 * The guide line above the record button: «slide up to lock», drawn as the
 * gesture rather than said in words. It has no arrowhead because it is not
 * pointing anywhere — it is the path the thumb takes, and the padlock below it
 * is where the path ends.
 */
export function SwipeUpArcIcon() {
  return (
    <svg width="40" height="18" viewBox="0 0 40 18" fill="none" aria-hidden="true">
      <path d="M2 16c5-12 31-12 36 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/**
 * The padlock, in both places the lock gesture is spoken about: the pill above
 * the record button that offers it, and the button that ends a take that took
 * it. One shape for one idea — a closed lock either way, since by the time it
 * is a button the recording is already locked.
 */
export function LockIcon({ size = 18 }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V7a4 4 0 018 0v4" />
    </svg>
  );
}
